import type { IDimensions } from '../types';

import { requestMutation } from '../lib/fasterdom/fasterdom';
import { IS_IOS } from './browser/windowEnvironment';
import { throttle } from './schedulers';

const WINDOW_ORIENTATION_CHANGE_THROTTLE_MS = 100;
const WINDOW_RESIZE_THROTTLE_MS = 250;

// On iOS the layout viewport (`window.innerHeight`) stays at its full size while the browser toolbars
// and the on-screen keyboard cover part of it; only the visual viewport reports what is actually on
// screen. The app has always sized its CSS from the visual viewport (`--vh` below), but every JS
// consumer read `window.innerHeight`, so the two disagreed by the height of whatever was covering the
// bottom — and `getIsKeyboardVisible` compared two values that never move on iOS, so it could not
// detect the keyboard at all. Measure once, here, and let both sides use it.
function getViewportHeight() {
  return IS_IOS
    ? window.visualViewport!.height + window.visualViewport!.pageTop
    : window.innerHeight;
}

let initialHeight = getViewportHeight();
let currentWindowSize = updateSizes();

const handleResize = throttle(() => {
  currentWindowSize = updateSizes();
}, WINDOW_RESIZE_THROTTLE_MS, true);

const handleOrientationChange = throttle(() => {
  initialHeight = getViewportHeight();
  handleResize();
}, WINDOW_ORIENTATION_CHANGE_THROTTLE_MS, false);

window.addEventListener('orientationchange', handleOrientationChange);
if (IS_IOS) {
  window.visualViewport!.addEventListener('resize', handleResize);
} else {
  window.addEventListener('resize', handleResize);
}

export function updateSizes(): IDimensions {
  const height = getViewportHeight();

  requestMutation(() => {
    const vh = height * 0.01;
    document.documentElement.style.setProperty('--vh', `${vh}px`);
  });

  return {
    width: window.innerWidth,
    height,
  };
}

const windowSize = {
  get: () => currentWindowSize,
  getIsKeyboardVisible: () => initialHeight > currentWindowSize.height,
};

export default windowSize;
